/**
 * The record header, split into three exports the chrome arranges across two
 * rows:
 *
 * - `RecordHeaderContent` — the type chip row (back chevron, icon, label,
 *   copy-id). Renders in the header's first row, beside the actions cluster.
 * - `RecordHeaderIdentity` — the identity block (avatar, title, sub-header).
 *   Renders full-width in the header's second row.
 * - `RecordHeaderActions` — the right cluster: expand, entity CTAs (Draft
 *   email, Add note, per-type extras) and the record overflow menu. The
 *   chrome's Header appends its close button after these, so the row always
 *   ends … actions, ✕.
 *
 * None of the three renders a close button: the chrome does, in every state.
 * All read the record and the shared header↔body state from
 * `RecordViewContext`; the back chevron reads `SurfaceDismissContext`.
 * See docs/claude/REACT_19_PATTERNS.md § 1.
 */

import { ArrowUpRight, Edit01, File06, Mail01 } from '@untitledui/icons';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useEmailCompose } from '@/contexts/EmailComposeContext';
import { useCanWrite } from '@/hooks/usePermissions';
import type { ResponsiveAction } from '@/lib/base/buttons';
import { ResponsiveActionBar } from '@/lib/base/buttons';
import { SurfaceBackButton } from '@/lib/base/overlays/surface-back-button';
import { useSurfaceDismiss } from '@/lib/base/overlays/surface-dismiss-context';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import {
  COMPANY,
  CONTACT,
  DEAL,
  EMAIL_RECIPIENT_TYPES,
} from '@/lib/crm/core/entityTypes';
import { getEntityPrimaryEmail } from '@/lib/crm/store/crmObjectStore';
import { getDisplayNameFromProperties } from '@/lib/crm/utils/getDisplayName';
import { getTypeLabel } from '@/lib/crm/utils/getTypeLabel';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { CrmObjectAvatar } from '../components/CrmObjectAvatar';
import { useEntityTypeSchema } from '../hooks/useEntityTypeSchema';
import { CopyEntityIdButton } from '../record/components/CopyEntityIdButton';
import { EditableRecordTitle } from '../record/components/EditableRecordTitle';
import { SlideoutHeaderActions } from '../record/components/SlideoutHeaderActions';
import { useBuildCustomEntityConfig } from '../record/registry/buildCustomEntityConfig';
import { getEntityTypeIconComponent } from '../utils/icons/EntityTypeIcon';
import { useRecordDraft } from './contexts/RecordDraftContext';
import {
  useRecordView,
  useRecordViewActions,
} from './contexts/RecordViewContext';
import { useEntityHeaderActions } from './hooks/useEntityHeaderActions';
import { useNoteTabActions } from './notes/useNoteTabActions';
import { RECORD_HEADER_ROW_CLASS } from './record-layout';
import { SubHeaderSection } from './sections/sub-header-section';

const DRAFT_EMAIL_LABEL = 'Draft email';
const EMPTY_ACTIONS: ResponsiveAction[] = [];

/**
 * Everything both halves derive from the record: resolved config, the
 * display name (with the draft's "New <type>: <name>" form), the previous
 * name a rename is replacing, and the type chip.
 */
function useRecordHeaderModel() {
  const { object, typeLabelOverride } = useRecordView();
  const draft = useRecordDraft();
  const entityType = object.entity_type_name;
  // Resolve via the runtime config hook (not the static getEntityTypeConfig):
  // for registered types it returns the identical static registry config, but
  // for custom (org_*) types it synthesizes a real fullPageRoute so the
  // expand-to-full-page affordance works for custom objects too.
  const { config } = useBuildCustomEntityConfig(object.org_id, entityType);

  // A record that does not exist yet and has not been named is not "Unknown" —
  // that fallback is for a real record whose name failed to resolve. Keying on
  // the display-name property being genuinely absent (rather than string-
  // matching the fallback) keeps this honest. When the draft's payload already
  // carries the name the record WILL have (`ahoy_name`, first+last — the
  // formula property `ahoy_display_name` is only computed server-side once
  // the record exists), render it beside the label: "New company: CloudVault".
  // A record that does not exist yet also has no persisted entity id worth
  // copying, and every record action addresses a saved record; both hide
  // until the proposal is approved.
  const isUnsavedRecord = Boolean(draft?.isUnsaved);
  let displayName = config.getDisplayName(object);
  if (
    draft?.isUnsaved &&
    !getPropertyValue<string>(object.properties?.ahoy_display_name)
  ) {
    const newRecordLabel = `New ${config.displayName.toLowerCase()}`;
    const draftName = getDisplayNameFromProperties(
      object.properties,
      null,
      object.entity_type_name
    );
    displayName = draftName
      ? `${newRecordLabel}: ${draftName}`
      : newRecordLabel;
  }
  // `previousValues` holds raw unwrapped values (buildProjectedObject.ts), and
  // only for properties the proposal actually changes — a proposal that
  // re-sets the name to what it already was records nothing, so this is
  // absent rather than equal. The string guard is for the type, not the logic.
  const previousDisplayNameRaw = draft?.previousValues?.['ahoy_display_name'];
  const previousDisplayName =
    typeof previousDisplayNameRaw === 'string' && previousDisplayNameRaw !== ''
      ? previousDisplayNameRaw
      : undefined;

  // For entity types not in the registry (fallback displayName is 'Record'),
  // fetch the label from the schema API so we show e.g. "Role" instead of "Record".
  const { schema: entityTypeSchema } = useEntityTypeSchema(
    object.org_id,
    entityType
  );
  const entityTypeLabel = getTypeLabel(
    object,
    {},
    entityTypeSchema ? [entityTypeSchema] : []
  );

  return {
    object,
    config,
    entityType,
    isUnsavedRecord,
    displayName,
    previousDisplayName,
    TypeChipIcon: getEntityTypeIconComponent(entityType),
    // typeLabelOverride wins when set (builder create path before a proposal
    // fixes the concrete type); otherwise use the resolved entity type label.
    typeChipLabel: typeLabelOverride ?? entityTypeLabel,
    isCompact: config.headerVariant === 'compact',
  };
}

/** Type chip row: back chevron, icon, label, copy-id. */
export function RecordHeaderContent(): ReactElement {
  const { object, isUnsavedRecord, TypeChipIcon, typeChipLabel } =
    useRecordHeaderModel();

  return (
    <div className={RECORD_HEADER_ROW_CLASS} data-testid='record-header'>
      <SurfaceBackButton />
      <TypeChipIcon className='w-3.5 h-3.5 text-fg-quaternary' />
      <span className='text-[10px] md:text-xs font-semibold text-fg-quaternary'>
        {typeChipLabel}
      </span>
      {/* Internal-only, and deliberately after the type label rather than
          between it and the icon: those two read as one unit. */}
      {!isUnsavedRecord && <CopyEntityIdButton entityId={object.entity_id} />}
    </div>
  );
}

/**
 * Identity block: avatar, title, sub-header. Renders full-width in the
 * header's second row.
 */
export function RecordHeaderIdentity(): ReactElement | null {
  const {
    object,
    config,
    entityType,
    displayName,
    previousDisplayName,
    isCompact,
  } = useRecordHeaderModel();

  const showEntityAvatar = config.showAvatar !== false;
  // Notes hide the display name here — NoteRecordOverview renders the title
  // inline with the content, so showing it in the header duplicates it.
  const showIdentity = entityType !== 'ahoy_note';

  if (!showIdentity) {
    return null;
  }

  return (
    <div
      className={cx(
        'min-w-0 pt-3 pb-1',
        isCompact ? 'flex flex-col gap-0.5' : 'flex items-start gap-3'
      )}
      data-testid='record-header-identity'
    >
      {!isCompact && showEntityAvatar && (
        <CrmObjectAvatar object={object} size='sm' />
      )}
      <div className='flex min-w-0 flex-col gap-0.5'>
        <EditableRecordTitle
          object={object}
          config={config}
          entityType={entityType}
          displayName={displayName}
          previousName={previousDisplayName}
        />
        <SubHeaderSection object={object} />
      </div>
    </div>
  );
}

/**
 * Right cluster of the header row: expand, entity CTAs, record overflow.
 * Renders nothing on review surfaces (`hideActions`) beyond the expand
 * affordance, and never a close — the chrome appends that.
 */
export function RecordHeaderActions(): ReactElement | null {
  const { hideActions, notesEnabled, overviewEditMode } = useRecordView();
  const { setOverviewEditMode } = useRecordViewActions();
  const surface = useSurfaceDismiss();
  const navigate = useNavigate();
  const { openEmailCompose } = useEmailCompose();
  const {
    object,
    config,
    entityType,
    isUnsavedRecord,
    displayName,
    isCompact,
  } = useRecordHeaderModel();
  // "Add note" goes through the shared Notes-tab hook, so the header, the
  // record's Notes tab and the contact hover card all open the same composer
  // under the same create gate.
  const { startCreatingNote, canCreateNote } = useNoteTabActions(
    object.entity_id,
    object.entity_type_name
  );

  const isReadOnly = config.readOnly === true;

  // Full-page view is offered only for the three standard objects (contact,
  // company, deal) and custom objects (`org_` prefix) — not the other system
  // object types. config.fullPageRoute is still required so we never render
  // an expand button that can't navigate. `isUnsavedRecord`: the full-page
  // route resolves by entity id, so for a record that does not exist yet it
  // can only land on a not-found page.
  const supportsFullPage =
    entityType === CONTACT ||
    entityType === COMPANY ||
    entityType === DEAL ||
    entityType.startsWith('org_');
  const showExpand =
    supportsFullPage && Boolean(config.fullPageRoute) && !isUnsavedRecord;

  // Person-like entities get a "Draft email" affordance. Companies, deals,
  // meetings etc. aren't email recipients so they don't. Both entity CTAs
  // ("Draft email" proposes a send, "Add note" creates a note) are writes, so
  // a read-only seat (no `ahoy_default`) never sees them.
  const canWrite = useCanWrite();
  const showDraftEmail = canWrite && EMAIL_RECIPIENT_TYPES.has(entityType);
  const showAddNote = canWrite && notesEnabled && canCreateNote;

  const surfaceClose = surface?.close;
  const handleExpand = useCallback(() => {
    if (!config.fullPageRoute) {
      return;
    }
    const route = config.fullPageRoute
      .replace(':orgId', String(object.org_id))
      .replace(':recordId', object.entity_id);
    // Only surfaces that overlay the page need dismissing before navigating.
    surfaceClose?.();
    navigate(route);
  }, [
    config.fullPageRoute,
    object.org_id,
    object.entity_id,
    surfaceClose,
    navigate,
  ]);

  const handleSendEmail = useCallback(() => {
    const recipientEmail = getEntityPrimaryEmail(object);
    openEmailCompose({
      to: recipientEmail ? [recipientEmail] : undefined,
      recipientEntity: {
        entityId: object.entity_id,
        entityTypeName: object.entity_type_name,
        displayName,
      },
    });
  }, [openEmailCompose, object, displayName]);

  const handleOverviewEdit = useCallback(
    () => setOverviewEditMode(true),
    [setOverviewEditMode]
  );

  // Header-driven edit for overview overrides that edit in place (ahoy_note →
  // NoteRecordOverview, ahoy_task → TaskRecordOverview's title editor). Hidden
  // while editing: the in-place editor autosaves on blur, so there is no
  // separate "done" affordance to keep.
  const { overviewHeaderEditLabel } = config;
  const overviewEditAction = useMemo<ResponsiveAction | null>(() => {
    if (!overviewHeaderEditLabel || overviewEditMode) {
      return null;
    }
    return {
      key: 'overview-edit',
      label: overviewHeaderEditLabel,
      icon: Edit01,
      onClick: handleOverviewEdit,
      placement: 'inline' as const,
    };
  }, [overviewHeaderEditLabel, overviewEditMode, handleOverviewEdit]);

  // Per-entity-type header actions (meetings' force-record toggle, email
  // threads' Reply/Reply all, …). Which types contribute what lives in
  // useEntityHeaderActions — this header stays entity-agnostic. Email threads
  // move reply actions to the sticky footer instead, so they are suppressed
  // here by entity type.
  const perTypeActions = useEntityHeaderActions(object);
  const isEmailThread = entityType === 'ahoy_email_thread';
  const extraActions = useMemo<ResponsiveAction[]>(() => {
    if (isEmailThread) {
      return EMPTY_ACTIONS;
    }
    return overviewEditAction
      ? [overviewEditAction, ...perTypeActions]
      : perTypeActions;
  }, [isEmailThread, overviewEditAction, perTypeActions]);

  // Entity-level CTAs (Draft email, Add note) collected as data. They render
  // inline as buttons on desktop and collapse into the unified overflow
  // dropdown on mobile via ResponsiveActionBar (placement defaults to
  // 'inline'). Order matters — the first CTA leads the inline row / dropdown.
  // Per-type extras are merged after; each extra's own `placement` decides
  // where it lands.
  const allEntityCtas = useMemo<ResponsiveAction[]>(() => {
    const ctas: ResponsiveAction[] = [];
    if (showDraftEmail) {
      ctas.push({
        key: 'draft-email',
        label: DRAFT_EMAIL_LABEL,
        icon: Mail01,
        onClick: handleSendEmail,
      });
    }
    if (showAddNote) {
      ctas.push({
        key: 'add-note',
        label: 'Add note',
        icon: File06,
        onClick: startCreatingNote,
      });
    }
    return extraActions.length > 0 ? [...ctas, ...extraActions] : ctas;
  }, [
    showDraftEmail,
    showAddNote,
    handleSendEmail,
    startCreatingNote,
    extraActions,
  ]);

  // Whether the record's overflow actions (Research / Enrich / Delete) show.
  // `ahoy_note` is readOnly yet opts back in via `showRecordActionMenu` so
  // notes keep their kebab → Delete. When true, SlideoutHeaderActions runs
  // `useRecordActions` (and its fetches) — gating here keeps that scoped.
  // Compact headers never surfaced record overflow actions — entity CTAs only.
  const showRecordActions =
    (config.showRecordActionMenu ?? !isReadOnly) &&
    !isUnsavedRecord &&
    !isCompact;

  // Unified action bar: entity CTAs inline (collapse on mobile) + record
  // overflow actions in a single dropdown. `hideActions` short-circuits
  // BEFORE `showRecordActions`, so no branch mounts SlideoutHeaderActions and
  // its `useRecordActions` fetches — a review surface should not pay for
  // actions it will not show.
  let actionBar: ReactNode = null;
  if (!hideActions) {
    actionBar = showRecordActions ? (
      <SlideoutHeaderActions object={object} entityActions={allEntityCtas} />
    ) : (
      <ResponsiveActionBar
        actions={allEntityCtas}
        dropdownLabel='Object actions'
        inlineFrom='md'
      />
    );
  }

  const expand = showExpand ? (
    <Tooltip title='Open full page' placement='bottom'>
      <TooltipTrigger
        onPress={handleExpand}
        className='p-2 hover:bg-[var(--color-bg-tertiary)] rounded-lg transition-colors cursor-pointer'
        aria-label='Open full page view'
      >
        <ArrowUpRight className='w-5 h-5 text-fg-quaternary' />
      </TooltipTrigger>
    </Tooltip>
  ) : null;

  if (!expand && !actionBar) {
    return null;
  }

  // Compact: actions lead, expand trails; full: expand leads.
  return isCompact ? (
    <>
      {actionBar}
      {expand}
    </>
  ) : (
    <>
      {expand}
      {actionBar}
    </>
  );
}
